// One Web Search References result (contract §6ai): the "external, verify it"
// label, when and through which service it was retrieved, the AI summary (each
// point citing a numbered source — anything uncited was removed on the server),
// and the real search results with links that open the original page.
// Deliberately no "Insert into note": web content is not hospital-approved, so
// it is only ever copied by hand, with its link.
import { useRef } from 'react';
import { ClipboardCopy, ExternalLink, Globe, TriangleAlert } from 'lucide-react';
import { toast } from '@/hooks/useToast';
import { webPointWithSources, type WebSearchAnswer } from './api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2024-04-23" → "23 Apr 2024", "2024-05" → "May 2024", "2024" → "2024". Fixed abbreviations (Intl prints "Sept"). */
export function showPublished(p: string | null): string | null {
  if (!p) return null;
  const [y, m, d] = p.split('-');
  const mon = m ? MONTHS[Number(m) - 1] : undefined;
  if (!mon) return y ?? null;
  return d ? `${Number(d)} ${mon} ${y}` : `${mon} ${y}`;
}

const retrieved = (iso: string) => {
  const t = new Date(iso);
  return `${t.getDate()} ${MONTHS[t.getMonth()]} ${t.getFullYear()}, ${t.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
};

export function WebSearchAnswerView({ answer }: { answer: WebSearchAnswer }) {
  const refs = useRef<Record<number, HTMLLIElement | null>>({});
  const showSource = (n: number) => {
    const el = refs.current[n];
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    el?.classList.add('ring-2', 'ring-teal');
    setTimeout(() => el?.classList.remove('ring-2', 'ring-teal'), 1200);
  };
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast({ title: 'Copied with its link', variant: 'success' }); }
    catch { toast({ title: 'Could not copy', description: 'Select the text and copy it instead.', variant: 'danger' }); }
  };
  const s = answer.summary;

  return (
    <div className="flex flex-col gap-3 text-[13px]">
      <div className="flex items-start gap-1.5 rounded-lg border border-warn-ink/30 bg-warn-soft px-3 py-2 text-[12.5px] text-warn-ink">
        <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{answer.disclaimer}</span>
      </div>
      <div className="text-[11.5px] text-ink-muted">
        Retrieved {retrieved(answer.retrieved_at)} via {answer.provider_label}{answer.cached ? ' · repeated search, same results as a moment ago' : ''}
      </div>

      {answer.status === 'no_results' && (
        <div className="rounded-lg border border-border bg-muted px-3 py-2.5">
          <div className="font-semibold text-ink">No reliable results found</div>
          <div className="mt-0.5 text-ink-muted">
            Nothing on the trusted sites matched{answer.filtered_out ? ` (${answer.filtered_out} result${answer.filtered_out === 1 ? '' : 's'} from other sites were hidden)` : ''}.
            Try different or more general words, or ask the Hospital References.
          </div>
        </div>
      )}

      {s && answer.results.length > 0 && (
        <div>
          <div className="mb-1 font-semibold text-ink">Summary of these sources</div>
          {s.points.length > 0 ? (
            <ul className="flex list-none flex-col gap-1.5 pl-0">
              {s.points.map((p, i) => (
                <li key={i} className="group rounded-md px-1.5 py-1 hover:bg-muted">
                  <span className="text-ink">{p.text}</span>{' '}
                  {p.citations.map((n) => (
                    <button key={n} type="button" onClick={() => showSource(n)} aria-label={`Show source ${n}`}
                      className="mx-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded bg-info-soft px-1 align-[1px] text-[10.5px] font-semibold text-info-ink hover:bg-info-ink hover:text-white">
                      {n}
                    </button>
                  ))}
                  <button type="button" className="ml-1 inline-flex items-center gap-0.5 text-[11px] text-ink-muted opacity-70 hover:text-teal group-hover:opacity-100"
                    onClick={() => copy(webPointWithSources(p, answer.results))}>
                    <ClipboardCopy className="h-3 w-3" /> Copy
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-md bg-muted px-3 py-2 text-ink-muted">{s.reason || 'No summary was written.'}</div>
          )}
          {s.points.length > 0 && s.reason && <div className="mt-1 rounded-md bg-warn-soft px-3 py-1.5 text-[12px] text-warn-ink">Not covered: {s.reason}</div>}
          {s.removed_unsupported > 0 && (
            <div className="mt-1 text-[11.5px] text-ink-muted">
              {s.removed_unsupported} statement{s.removed_unsupported === 1 ? ' was' : 's were'} left out because {s.removed_unsupported === 1 ? 'it' : 'they'} could not be tied to a source below.
            </div>
          )}
        </div>
      )}

      {answer.results.length > 0 && (
        <div>
          <div className="mb-1 flex items-center gap-1 font-semibold text-ink"><Globe className="h-3.5 w-3.5" /> Sources ({answer.results.length})</div>
          <ol className="flex list-none flex-col gap-1.5 pl-0">
            {answer.results.map((r) => (
              <li key={r.n} ref={(el) => { refs.current[r.n] = el; }} className="rounded-md border border-border bg-card px-2.5 py-2 transition-shadow">
                <div className="flex items-start gap-2">
                  <span className="mt-[1px] inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded bg-info-soft px-1 text-[10.5px] font-semibold text-info-ink">{r.n}</span>
                  <div className="min-w-0 flex-1">
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="font-medium text-ink hover:text-teal hover:underline">{r.title}</a>
                    <div className="text-[11.5px] text-ink-muted">
                      {[r.domain, r.source_name, showPublished(r.published) ? `published ${showPublished(r.published)}` : null].filter(Boolean).join(' · ')}
                    </div>
                    {r.snippet && <p className="mt-1 line-clamp-4 text-[12px] leading-relaxed text-ink">{r.snippet}</p>}
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11.5px] font-medium text-teal hover:underline">
                      <ExternalLink className="h-3 w-3" /> Open source
                    </a>
                  </div>
                </div>
              </li>
            ))}
          </ol>
          {answer.filtered_out > 0 && answer.status === 'ok' && (
            <div className="mt-1 text-[11.5px] text-ink-muted">{answer.filtered_out} result{answer.filtered_out === 1 ? '' : 's'} from sites outside the trusted list {answer.filtered_out === 1 ? 'was' : 'were'} hidden.</div>
          )}
        </div>
      )}
    </div>
  );
}
